import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from "../includes/sidebar";
import { Topbar } from "../includes/topbar";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import { Badge } from "../../components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import {
    MoreHorizontal,
    Search,
    Check,
    X,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2,
    RefreshCw,
    AlertCircle,
    Eye,
    UserCheck,
    UserX,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { comptesApi, clientsApi } from '../../api';
import type { Compte } from '../../api/clients.api';
import { toast } from '../../hooks/use-toast';



const typeBadgeVariant = (type: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (type) {
        case 'BS':
            return 'default';
        case 'SOL':
            return 'secondary';
        default:
            return 'outline';
    }
};

export default function UnvalidatedPage() {
    const { t } = useTranslation();
    // State
    const [comptes, setComptes] = useState<Compte[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalComptes, setTotalComptes] = useState(0);

    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Modal state
    const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
    const [selectedCompte, setSelectedCompte] = useState<Compte | null>(null);
    const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
    const [compteToReject, setCompteToReject] = useState<Compte | null>(null);
    const [isValidateDialogOpen, setIsValidateDialogOpen] = useState(false);
    const [compteToValidate, setCompteToValidate] = useState<Compte | null>(null);
    const [isValidating, setIsValidating] = useState<string | null>(null);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch pending comptes (online created)
    const fetchComptes = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await comptesApi.list({
                page: currentPage,
                limit: itemsPerPage,

                // statut filter removed - backend auto-filters by client.statut='pending_verification' when onlineCreated=true
                search: debouncedSearch || undefined,
                onlineCreated: true, // Filter by client.onlineCreated
            });
            setComptes(response.comptes || []);
            setTotalComptes(response.total || 0);
        } catch (err: any) {
            const message = err.response?.data?.message || t('accounts:unvalidated.table.error');
            setError(message);
            console.error('Fetch pending comptes error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, itemsPerPage, debouncedSearch, t]);

    useEffect(() => {
        fetchComptes();
    }, [fetchComptes]);

    // Pagination
    const totalPages = Math.ceil(totalComptes / itemsPerPage);

    // Actions
    const openValidateDialog = (compte: Compte) => {
        setCompteToValidate(compte);
        setIsValidateDialogOpen(true);
    };

    const confirmValidate = async () => {
        if (!compteToValidate?.client) {
            toast({
                title: t('common:error'),
                description: t('accounts:unvalidated.dialogs.validate.clientUnavailable'),
                type: 'error',
            });
            return;
        }

        setIsValidating(compteToValidate.id);
        try {
            // Activate the client and all their accounts
            await clientsApi.activateAll(compteToValidate.client.id);
            toast({
                title: t('common:success'),
                description: t('accounts:unvalidated.dialogs.validate.success', { code: compteToValidate.code }),
                type: 'success',
            });
            setIsValidateDialogOpen(false);
            setCompteToValidate(null);
            setIsViewDetailsOpen(false);
            fetchComptes();
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('accounts:unvalidated.dialogs.validate.error'),
                type: 'error',
            });
        } finally {
            setIsValidating(null);
        }
    };

    const handleReject = async () => {
        if (!compteToReject?.client) {
            toast({
                title: t('common:error'),
                description: t('accounts:unvalidated.dialogs.reject.clientUnavailable'),
                type: 'error',
            });
            return;
        }

        setIsValidating(compteToReject.id);
        try {
            // Rollback the client registration
            await clientsApi.rollback(compteToReject.client.id);
            toast({
                title: t('common:success'),
                description: t('accounts:unvalidated.dialogs.reject.success', { code: compteToReject.code }),
                type: 'success',
            });
            setIsRejectDialogOpen(false);
            setCompteToReject(null);
            fetchComptes();
        } catch (err: any) {
            toast({
                title: t('common:error'),
                description: err.response?.data?.message || t('accounts:unvalidated.dialogs.reject.error'),
                type: 'error',
            });
        } finally {
            setIsValidating(null);
        }
    };

    const handleViewDetails = (compte: Compte) => {
        setSelectedCompte(compte);
        setIsViewDetailsOpen(true);
    };

    const openRejectDialog = (compte: Compte) => {
        setCompteToReject(compte);
        setIsRejectDialogOpen(true);
    };

    // Format helpers
    const formatBalance = (value: number | undefined): string => {
        if (value === undefined || value === null) return '0.00';
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value));
    };

    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto space-y-4 p-8 pt-6">
                    <div className="flex items-center justify-between space-y-2">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink className="capitalize">{t('accounts:unvalidated.breadcrumb.accounts')}</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage className="capitalize">{t('accounts:unvalidated.breadcrumb.unvalidated')}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <div className="space-y-1">
                                <CardTitle className="flex items-center gap-2">
                                    {t('accounts:unvalidated.title')}
                                    {totalComptes > 0 && (
                                        <Badge variant="secondary">{totalComptes}</Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    {t('accounts:unvalidated.description')}
                                </CardDescription>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchComptes}
                                disabled={isLoading}
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                                {t('common:buttons.refresh')}
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {/* Filters */}
                            <div className="flex flex-wrap items-center py-4 gap-2">
                                <div className="relative w-full max-w-sm">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder={t('accounts:unvalidated.searchPlaceholder')}
                                        className="pl-8"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Error state */}
                            {error && (
                                <div className="flex items-center gap-2 p-4 mb-4 text-red-600 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="h-5 w-5" />
                                    <span>{error}</span>
                                    <Button variant="link" className="ml-auto text-red-600" onClick={fetchComptes}>
                                        {t('common:buttons.retry', { defaultValue: 'Eseye ankò' })}
                                    </Button>
                                </div>
                            )}

                            {/* Loading state */}
                            {isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                                    <span className="ml-2 text-gray-500">{t('common:loading')}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-md border">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="text-center">{t('accounts:unvalidated.table.account')}</TableHead>
                                                    <TableHead className="text-center">{t('accounts:unvalidated.table.client')}</TableHead>
                                                    <TableHead className="text-center">{t('common:labels.balance')}</TableHead>
                                                    <TableHead className="text-center">{t('accounts:unvalidated.table.date')}</TableHead>
                                                    <TableHead className="text-center">{t('common:labels.actions')}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {comptes.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                                                            <div className="flex flex-col items-center gap-2">
                                                                <UserCheck className="h-12 w-12 text-green-300" />
                                                                <span className="text-lg font-medium">
                                                                    {t('accounts:unvalidated.table.noResults.title')}
                                                                </span>
                                                                <span className="text-sm">
                                                                    {t('accounts:unvalidated.table.noResults.description')}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    comptes.map((compte) => (
                                                        <TableRow key={compte.id} className="bg-amber-50/50">
                                                            <TableCell>
                                                                <div>
                                                                    <div className="font-mono font-medium">
                                                                        {compte.code}
                                                                    </div>
                                                                    <div className="flex items-center gap-1 mt-1">
                                                                        <Badge variant={typeBadgeVariant(compte.type)} className="text-xs">
                                                                            {compte.type}
                                                                        </Badge>
                                                                        <span className="text-xs text-gray-500">{compte.devise}</span>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                {compte.client ? (
                                                                    <div>
                                                                        <div className="font-medium">
                                                                            {compte.client.prenom} {compte.client.nom}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {compte.client.telephone || compte.client.email || '-'}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-gray-400">-</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="font-mono font-bold text-right">
                                                                    {formatBalance(compte.soldeBS)}
                                                                    <span className="text-xs text-gray-400 ml-1">{compte.devise}</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-center">
                                                                {formatDate(compte.createdAt)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="default"
                                                                        className="bg-green-600 hover:bg-green-700"
                                                                        onClick={() => openValidateDialog(compte)}
                                                                        disabled={isValidating === compte.id}
                                                                    >
                                                                        {isValidating === compte.id ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : (
                                                                            <>
                                                                                <Check className="h-4 w-4 mr-1" />
                                                                                {t('accounts:unvalidated.actions.validate')}
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                    <DropdownMenu>
                                                                        <DropdownMenuTrigger asChild>
                                                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                                                                <MoreHorizontal className="h-4 w-4" />
                                                                            </Button>
                                                                        </DropdownMenuTrigger>
                                                                        <DropdownMenuContent align="end">
                                                                            <DropdownMenuLabel>{t('common:labels.actions')}</DropdownMenuLabel>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem onClick={() => handleViewDetails(compte)}>
                                                                                <Eye className="mr-2 h-4 w-4" />
                                                                                {t('common:buttons.viewDetails')}
                                                                            </DropdownMenuItem>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onClick={() => openRejectDialog(compte)}
                                                                                className="text-red-600 focus:text-red-600"
                                                                            >
                                                                                <X className="mr-2 h-4 w-4" />
                                                                                {t('accounts:unvalidated.actions.reject')}
                                                                            </DropdownMenuItem>
                                                                        </DropdownMenuContent>
                                                                    </DropdownMenu>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    {comptes.length > 0 && (
                                        <div className="flex items-center justify-end space-x-2 py-4">
                                            <div className="flex items-center space-x-2">
                                                <p className="text-sm font-medium">{t('common:labels.rowsPerPage')}</p>
                                                <Select
                                                    value={`${itemsPerPage}`}
                                                    onValueChange={(value) => {
                                                        setItemsPerPage(Number(value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <SelectTrigger className="h-8 w-[70px]">
                                                        <SelectValue placeholder={itemsPerPage} />
                                                    </SelectTrigger>
                                                    <SelectContent side="top">
                                                        {[5, 10, 20, 50].map((pageSize) => (
                                                            <SelectItem key={pageSize} value={`${pageSize}`}>
                                                                {pageSize}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="flex w-[150px] items-center justify-center text-sm font-medium">
                                                {totalComptes > 0 ? (
                                                    `${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalComptes)} ` + t('common:labels.of') + ` ${totalComptes}`
                                                ) : (
                                                    "0 " + t('common:labels.of') + " 0"
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Button
                                                    variant="outline"
                                                    className="hidden h-8 w-8 p-0 lg:flex"
                                                    onClick={() => setCurrentPage(1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    <ChevronsLeft className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <ChevronLeft className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <ChevronRight className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="hidden h-8 w-8 p-0 lg:flex"
                                                    onClick={() => setCurrentPage(totalPages)}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <ChevronsRight className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Reject Confirmation Dialog */}
            <AlertDialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader className="text-center space-y-4">
                        <div className="mx-auto bg-gradient-to-br from-red-100 to-rose-100 p-4 rounded-full w-fit">
                            <AlertCircle className="h-10 w-10 text-red-600" />
                        </div>
                        <AlertDialogTitle className="text-xl font-bold text-gray-900">
                            {t('accounts:unvalidated.dialogs.reject.title')}
                        </AlertDialogTitle>
                    </AlertDialogHeader>

                    <div className="py-4 space-y-4">
                        {/* Client info card */}
                        <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-xl border border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="bg-white p-2 rounded-full shadow-sm">
                                    <UserX className="h-6 w-6 text-gray-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">
                                        {compteToReject?.client?.prenom} {compteToReject?.client?.nom}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {compteToReject?.client?.telephone || compteToReject?.client?.email}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Warning */}
                        <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                            <p className="text-sm font-medium text-red-800 mb-2">⚠️ {t('common:warning')}</p>
                            <p className="text-sm text-red-700">
                                {t('accounts:unvalidated.dialogs.reject.warning')}
                                <strong className="block mt-1">{t('accounts:unvalidated.dialogs.reject.irreversible')}</strong>
                            </p>
                        </div>
                    </div>

                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel
                            onClick={() => setCompteToReject(null)}
                            className="w-full sm:w-auto"
                        >
                            {t('common:buttons.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleReject}
                            className="w-full sm:w-auto bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white font-semibold"
                            disabled={isValidating === compteToReject?.id}
                        >
                            {isValidating === compteToReject?.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <X className="h-4 w-4 mr-2" />
                            )}
                            {t('accounts:unvalidated.dialogs.reject.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Validate Confirmation Dialog */}
            <AlertDialog open={isValidateDialogOpen} onOpenChange={setIsValidateDialogOpen}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader className="text-center space-y-4">
                        <div className="mx-auto bg-gradient-to-br from-green-100 to-emerald-100 p-4 rounded-full w-fit">
                            <UserCheck className="h-10 w-10 text-green-600" />
                        </div>
                        <AlertDialogTitle className="text-xl font-bold text-gray-900">
                            {t('accounts:unvalidated.dialogs.validate.title')}
                        </AlertDialogTitle>
                    </AlertDialogHeader>

                    <div className="py-4 space-y-4">
                        {/* Client info card */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                            <div className="flex items-center gap-3">
                                <div className="bg-white p-2 rounded-full shadow-sm">
                                    <UserCheck className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">
                                        {compteToValidate?.client?.prenom} {compteToValidate?.client?.nom}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {compteToValidate?.client?.telephone || compteToValidate?.client?.email}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* What will happen */}
                        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                            <p className="text-sm font-medium text-green-800 mb-2">{t('accounts:unvalidated.dialogs.validate.plan')}</p>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-green-700">
                                    <Check className="h-4 w-4" />
                                    <span>{t('accounts:unvalidated.dialogs.validate.step1')}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-green-700">
                                    <Check className="h-4 w-4" />
                                    <span>{t('accounts:unvalidated.dialogs.validate.step2')}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel
                            onClick={() => setCompteToValidate(null)}
                            className="w-full sm:w-auto"
                        >
                            {t('common:buttons.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmValidate}
                            className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold"
                            disabled={isValidating === compteToValidate?.id}
                        >
                            {isValidating === compteToValidate?.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Check className="h-4 w-4 mr-2" />
                            )}
                            {t('accounts:unvalidated.dialogs.validate.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* View Details Modal */}
            <Dialog open={isViewDetailsOpen} onOpenChange={setIsViewDetailsOpen}>
                <DialogContent className="w-[95vw] max-w-[480px] max-h-[85vh] overflow-y-auto">
                    <DialogHeader className="pb-3 border-b">
                        <DialogTitle className="text-lg font-semibold">
                            {selectedCompte?.client?.prenom} {selectedCompte?.client?.nom}
                        </DialogTitle>
                        <p className="text-sm text-gray-500">
                            {t('accounts:unvalidated.table.accountCode')}: <span className="font-mono font-bold">{selectedCompte?.code}</span> • {formatDate(selectedCompte?.createdAt)}
                        </p>
                    </DialogHeader>

                    {selectedCompte && (
                        <div className="space-y-4 py-2">
                            {/* Kliyan */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                <div>
                                    <span className="text-gray-500">{t('common:labels.phone')}</span>
                                    <p className="font-medium">{selectedCompte.client?.telephone || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">{t('accounts:unvalidated.profile.kyc')}</span>
                                    <p className="font-medium capitalize">{selectedCompte.client?.kycStatus || '-'}</p>
                                </div>
                                {selectedCompte.client?.email && (
                                    <div className="col-span-2">
                                        <span className="text-gray-500">{t('common:labels.email')}</span>
                                        <p className="font-medium break-all">{selectedCompte.client.email}</p>
                                    </div>
                                )}
                                {(selectedCompte.client?.adresse || selectedCompte.client?.ville) && (
                                    <div className="col-span-2">
                                        <span className="text-gray-500">{t('common:labels.address')}</span>
                                        <p className="font-medium">
                                            {[selectedCompte.client?.adresse, selectedCompte.client?.ville].filter(Boolean).join(', ')}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <hr className="border-gray-100" />

                            {/* Kont */}
                            <div className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
                                <div>
                                    <span className="text-gray-500">{t('common:labels.type')}</span>
                                    <p className="font-semibold">{selectedCompte.type}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">{t('common:labels.currency')}</span>
                                    <p className="font-medium">{selectedCompte.devise}</p>
                                </div>
                                <div>
                                    <span className="text-gray-500">{t('common:labels.status')}</span>
                                    <p className="font-medium text-amber-600 capitalize">{selectedCompte.statut}</p>
                                </div>
                            </div>

                            {/* Balans */}
                            <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                                <span className="text-gray-600 text-sm">{t('common:labels.balance')}</span>
                                <span className="font-mono font-bold text-lg text-green-700">
                                    {formatBalance(selectedCompte.soldeBS)} {selectedCompte.devise}
                                </span>
                            </div>

                            {/* Bouton */}
                            <div className="flex gap-2 pt-2">
                                <Button
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                    onClick={() => openValidateDialog(selectedCompte)}
                                    disabled={isValidating === selectedCompte.id}
                                >
                                    {isValidating === selectedCompte.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                    )}
                                    {t('accounts:unvalidated.actions.validate')}
                                </Button>
                                <Button
                                    variant="destructive"
                                    className="flex-1"
                                    onClick={() => {
                                        setIsViewDetailsOpen(false);
                                        openRejectDialog(selectedCompte);
                                    }}
                                >
                                    <X className="h-4 w-4 mr-2" />
                                    {t('accounts:unvalidated.actions.reject')}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
